import Vue, { CreateElement } from '@src/vue'
import { Component } from 'vue-property-decorator'

import { Subtitle1 } from '@src/elements/typography/typography'

import { WorkoutCardInfo } from '@src/components/card/workout-card/workout-card-info/workout-card-info'
import { TrainingCardInfo } from '@src/components/card/training-card/training-card-info/training-card-info'
import { ActiveTrainingWorkout } from '@src/routes/members/components/active-trainings-workouts/active-section/active-training-workout'
import { ActivateWorkouts } from '@src/routes/members/components/active-trainings-workouts/active-section/activate-workouts'
import { ActivateTrainingPlans } from '@src/routes/members/components/active-trainings-workouts/active-section/activate-training-plans'
import { Spacer } from '@src/elements/spacer/spacer'
import { Button, MenuButton } from '@src/elements/button/button'
import { Popover } from '@src/components/popover/popover'
import { OptionGroup } from '@src/elements/select/option-group'

import WorkoutData, { WorkoutType } from '@src/data/workout-data'
import TrainingPlan from '@src/data/training-plan'
import Member from '@src/data/member'

import EventBus from '@src/lib/event_bus'
import { track } from '@src/util/functions'
import SessionManager from '@src/lib/session_manager'
import { TrainingPlanService } from '@src/service/training-plan'
import { Loader } from '@src/components/loading-screen/loader/loader'

import './active-trainings-workouts.sass'

@Component({
  computed: {
    member () {
      return this.$store.state.selectedMember || this.$store.state.memberProfile
    }
  }
})
export class ActiveTrainingsWorkouts extends Vue {

  protected member: Member
  protected upcomingTPs: TrainingPlan[] = []
  protected loading = false

  constructor () {
    super()
    track('/members/training')
  }

  created () {
    EventBus.on('reloadSelectedMember', this.loadUpcomingTps)
    return this.loadUpcomingTps()
  }

  beforeDestroy () {
    EventBus.off('reloadSelectedMember', this.loadUpcomingTps)
  }

  async loadUpcomingTps () {
    this.loading = true
    this.upcomingTPs = []
    await this.getUpcomingTPS()
    this.loading = false
    this.scrollToUpcoming()
  }

  updated () {
    this.setDocumentTitle()
    this.scrollToUpcoming()
  }

  scrollToUpcoming () {
    if (this.$route.query.section === 'upcoming_plans' && this.$refs.upcoming) {
      const el = this.$refs.upcoming as HTMLElement
      el.scrollIntoView()
      this.$route.query.section = ''
    }
  }

  setDocumentTitle () {
    document.title = `${this.$root.$t('PIXFORMANCE_PLATFORM')} - ${this.$root.$t('MEMBERS')} - ${this.$root.$t('PAGE.MEMBERS.DETAIL.TABS.TRAINING')}`
  }

  goToWorkoutBuilder (workoutType: WorkoutType) {
    this.$store.dispatch('removeNewWorkout')
    this.$store.dispatch('setWorkoutType', workoutType)
    this.$store.dispatch('setMemberForActiveWorkout', this.member)
    this.$router.push(`${SessionManager.Instance.getBaseRoute()}/workout`)
  }

  goToTrainingPlanBuilder () {
    this.$store.dispatch('removeNewTrainingPlan')
    this.$router.push(`${SessionManager.Instance.getBaseRoute()}/trainingPlan`)
  }

  changeSelection (type: string, item: WorkoutData | TrainingPlan) {
    EventBus.trigger('expandMemberTraining', type, item.id)
  }

  render (h: CreateElement) {
    let activeWorkout = this.member.activeWorkout && !this.member.currentTrainingPlan
    let activeTrainingPlan = this.member.currentTrainingPlan

    return <div class='active-trainings-workouts scroll-content'>
      {this.renderCreateOptions(h)}

      <div class='subtitleWrapper'>
        <div class={'dot ' + (activeWorkout ? 'active' : 'inactive')}/>
        <Subtitle1>{this.$t('PAGE.MEMBERS.TRAINING.ACTIVE_STATION_WORKOUT')}</Subtitle1>
      </div >
      <ActiveTrainingWorkout title='PAGE.MEMBERS.TRAINING.SET_ACTIVE_WORKOUT'
                             noSelectionPlaceholder='PAGE.MEMBERS.TRAINING.NO_WORKOUT'
                             content={<ActivateWorkouts type='custom' />}
                             activeItem={this.member.activeWorkout ? <WorkoutCardInfo workout={this.member.activeWorkout} member={this.member} type='active' /> : null}
                             type='custom' />
      {this.member.activeWorkout ?
        <div class='active-workout-button-wrapper'>
          <Button type='elevated' label={this.$t('PAGE.MEMBERS.TRAINING.CHANGE_WORKOUT')} click={
            () => this.changeSelection('custom', this.member.activeWorkout)} />
        </div> :
          ''}
      <Spacer />
      <Spacer />

      <div class='subtitleWrapper'>
          <div class={'dot ' + (activeTrainingPlan ? 'active' : 'inactive')}/>
          <Subtitle1>{this.$t('PAGE.MEMBERS.TRAINING.ACTIVE_TRAINING_PLAN')}</Subtitle1>
        </div>
        <ActiveTrainingWorkout title='PAGE.MEMBERS.TRAINING.SET_ACTIVE_TRAINING_PLAN'
                               noSelectionPlaceholder='PAGE.MEMBERS.TRAINING.NO_TRAINING_PLAN'
                               content={<ActivateTrainingPlans />}
                               activeItem={this.member.currentTrainingPlan ? <TrainingCardInfo type='active' training={this.member.currentTrainingPlan} /> : null}
                               type='training-plan'
                               class='active-training-plan-wrapper' />
      {this.member.currentTrainingPlan ?
        <div class='active-workout-button-wrapper'>
          <Button class='change-workout' type='elevated' label={this.$t('PAGE.MEMBERS.TRAINING.CHANGE_PLAN')} click={
            () => this.changeSelection('training-plan', this.member.currentTrainingPlan)} />
        </div> : ''}
      <Spacer />
      {this.loading && <Loader/>}
      {this.renderUpcomingTPS(h)}
    </div >
  }

  renderUpcomingTPS (h: CreateElement) {
    if (!this.upcomingTPs.length) return ''
    return (<div class='upcoming-section' ref='upcoming'>
      <Subtitle1 class='section-title'>{this.$t('PAGE.MEMBERS.TRAINING.UPCOMING_TRAINING_PLAN')}</Subtitle1>
      {this.upcomingTPs.map((tp) => <TrainingCardInfo type='active' training={tp} updateCallback={this.loadUpcomingTps} key={tp.id}/>)}
    </div>)
  }

  getUpcomingTPS () {
    return new TrainingPlanService().getTrainingPlans(1, 100, 'member', '', this.member.id, false, true)
      .then(response => {
        response.trainingPlans.forEach((tp: TrainingPlan) => this.upcomingTPs.push(tp))
        return Promise.resolve()
      }).catch(() => console.log('Failed to load upcoming tps'))
  }

  renderCreateOptions (h: CreateElement) {
    if (!SessionManager.Instance.isAssistant()) {
      return <MenuButton class='workout-btn'>
        <Button slot='button' label={this.$t('NEW')} type='elevated'/>
        <Popover>
          <OptionGroup>
            <div ref='newWO' onClick={() => {
              this.goToWorkoutBuilder(WorkoutType.CUSTOM)
            }}>{this.$t('PAGE.MEMBERS.TRAINING.ACTIVE_STATION_WORKOUT')}</div>
            {/*Hide home workouts*/}
            {/*<div onClick={() => {*/}
            {/*  this.goToWorkoutBuilder(WorkoutType.HOME)*/}
            {/*}}>{this.$t('PAGE.MEMBERS.TRAINING.ACTIVE_HOME_WORKOUT')}</div>*/}
            <div ref='newTP'
                 onClick={this.goToTrainingPlanBuilder}>{this.$t('PAGE.MEMBERS.TRAINING.ACTIVE_TRAINING_PLAN')}</div>
          </OptionGroup>
        </Popover>
      </MenuButton>
    }
    return null
  }

  beforeMount () {
    // member will be used in the create/edit training plan builder
    this.$store.dispatch('setMemberForActiveTraining', this.member)
  }
}
